/**
 * KundenPhasenLeiste — horizontale Fortschrittsleiste für das Kunden-Portal.
 *
 * Issue #424. Zeigt die 6 vereinfachten Kunden-Phasen, die aktuelle Phase
 * blau hervorgehoben. Mobile-first (≤375px funktional).
 *
 * Akzeptiert ausschliesslich whitelisted Felder aus der Progress-API —
 * keine internen Begriffe wie „Kürzung", „Klage", „QS", „Stellungnahme".
 */

import { Check } from 'lucide-react';
import {
  KUNDEN_PHASE_LABELS,
  type KundenPhase,
  type KundenProgressResponse,
} from '@/lib/kunden-phase';

const ALLE_PHASEN: KundenPhase[] = [1, 2, 3, 4, 5, 6];

interface Props {
  progress: KundenProgressResponse;
}

function formatLetzteAktualisierung(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('de-DE');
  } catch {
    return null;
  }
}

export function KundenPhasenLeiste({ progress }: Props) {
  const aktuell = progress.kunden_phase;
  const datum = formatLetzteAktualisierung(progress.letzte_aktualisierung);

  return (
    <div
      data-testid="kunden-phasen-leiste"
      className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 shadow-sm"
    >
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h3 className="text-sm font-bold text-gray-900">Status Ihres Falls</h3>
        {datum && (
          <span className="text-[11px] text-gray-400">
            Letzte Aktualisierung: {datum}
          </span>
        )}
      </div>

      <p className="text-sm text-gray-700 mb-4">{progress.kunden_phase_text}</p>

      {/* Phasen-Balken: horizontal mit Punkten + Verbindungslinien.
          Auf schmalen Screens (≤375px) wird die Schrift kleiner und nur
          die aktuelle Phase trägt das ausführliche Label. */}
      <ol
        className="flex items-start justify-between gap-1"
        aria-label="Phasen"
      >
        {ALLE_PHASEN.map((phase, idx) => {
          const istErledigt = phase < aktuell;
          const istAktiv = phase === aktuell;
          const istAktuellOderErledigt = phase <= aktuell;

          return (
            <li key={phase} className="flex-1 flex flex-col items-center min-w-0">
              <div className="w-full flex items-center">
                {/* Linie links (nicht für erste Phase) */}
                {idx > 0 && (
                  <div
                    aria-hidden="true"
                    className={`flex-1 h-0.5 ${
                      phase <= aktuell ? 'bg-[#9B1C1C]' : 'bg-gray-200'
                    }`}
                  />
                )}
                <div
                  data-testid={`kunden-phase-dot-${phase}`}
                  data-active={istAktiv ? 'true' : undefined}
                  data-completed={istErledigt ? 'true' : undefined}
                  className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
                    istAktiv
                      ? 'bg-[#9B1C1C] text-white ring-4 ring-[#9B1C1C]/20'
                      : istErledigt
                        ? 'bg-[#9B1C1C] text-white'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {istErledigt ? <Check className="w-3.5 h-3.5" /> : phase}
                </div>
                {/* Linie rechts (nicht für letzte Phase) */}
                {idx < ALLE_PHASEN.length - 1 && (
                  <div
                    aria-hidden="true"
                    className={`flex-1 h-0.5 ${
                      phase < aktuell ? 'bg-[#9B1C1C]' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
              <span
                className={`mt-2 text-[10px] sm:text-[11px] text-center leading-tight ${
                  istAktuellOderErledigt
                    ? istAktiv
                      ? 'text-[#9B1C1C] font-bold'
                      : 'text-gray-700'
                    : 'text-gray-400'
                }`}
              >
                {KUNDEN_PHASE_LABELS[phase]}
              </span>
            </li>
          );
        })}
      </ol>

      {progress.warten_auf_kunde && (
        <div
          data-testid="warten-auf-kunde"
          className="mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl"
        >
          <div className="text-amber-700 text-xs">
            <span className="font-bold">Wir warten auf Sie:</span>{' '}
            {progress.warten_auf_kunde}
          </div>
        </div>
      )}

      {progress.kunden_nachricht && (
        <div
          data-testid="kunden-nachricht"
          className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700"
        >
          {progress.kunden_nachricht}
        </div>
      )}
    </div>
  );
}

export default KundenPhasenLeiste;
